import { useLocation, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Compass, House } from 'lucide-react';
import { Button } from '@/components/ui/NewButton';

/**
 * Branded fallback the router renders for any unmatched URL (via the router's
 * `defaultNotFoundComponent`). Like {@link RouteErrorFallback} it renders inside
 * the matched route's `<Outlet />`, so it sits within the app shell when one is
 * present and otherwise fills the available space.
 */
export function NotFoundFallback() {
  const router = useRouter();
  const { pathname } = useLocation();

  return (
    <div className="flex h-full min-h-[400px] w-full flex-col items-center justify-center bg-white p-6">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-crimson-500/10">
          <Compass className="h-6 w-6 text-crimson-500" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Page not found</h2>
        <p className="mt-2 text-sm text-gray-600">
          We couldn't find the page you're looking for. It may have been moved, renamed, or never
          existed.
        </p>

        {pathname && (
          <p className="mt-4 break-all rounded-sm bg-gray-100 px-3 py-2 font-mono text-xs text-gray-500">
            {pathname}
          </p>
        )}

        <div className="mt-6 flex items-center justify-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            icon={ArrowLeft}
            text="Go back"
            onClick={() => router.history.back()}
          />
          <Button
            type="button"
            size="sm"
            icon={House}
            text="Go home"
            onClick={() => router.navigate({ to: '/home' })}
          />
        </div>
      </div>
    </div>
  );
}
